/**
 * Small pieces shared by the scenario workspace and the Priced Scenarios list:
 * the status pill, the type tag, the amber "unpriced changes" pill and the
 * compact header buttons.
 */
import Box from "@mui/material/Box";

import { slate, status as STATUS } from "../../../theme/colors";
import { SZ, PrimaryButton, SecondaryButton } from "../sizerUi";

export const NUM = { fontVariantNumeric: "tabular-nums" };

/* A step stronger than the plain status pairs, plus a hairline in the same
   hue: the pill sits on the gray page and the frosted workspace header, where
   the 50-level tints (e.g. #ECFDF5) all but disappear. Text stays the status
   `fg`. */
const STATUS_TONES = {
  Active: { bg: "#D1FAE5", fg: STATUS.success.fg, line: "#A7F3D0" },
  Converted: { bg: "#DCE6F3", fg: STATUS.info.fg, line: "#C3D2E6" },
  Archived: { bg: slate[200], fg: slate[600], line: slate[300] },
};

/** A scenario's status as a dot + tinted pill. The label is always text. */
export const ScenarioStatusPill = ({ status }) => {
  const t = STATUS_TONES[status] || STATUS_TONES.Archived;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        px: 1,
        py: "2px",
        borderRadius: "99px",
        bgcolor: t.bg,
        color: t.fg,
        border: `1px solid ${t.line}`,
        "&::before": {
          content: '""',
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: t.fg,
        },
      }}
    >
      {status || "—"}
    </Box>
  );
};

/** Which sizer owns a scenario — RTL / GUC / DSCR — as a small mono tag. */
export const TypeTag = ({ children }) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      fontFamily: SZ.mono,
      fontSize: 10.5,
      fontWeight: 500,
      color: SZ.ink600,
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: "5px",
      px: "6px",
      lineHeight: "17px",
    }}
  >
    {children}
  </Box>
);

/** Amber "2 unpriced changes" — the form no longer says what the numbers say. */
export const ChangesPill = ({ count }) =>
  count > 0 ? (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        px: 1,
        py: "2px",
        borderRadius: "99px",
        bgcolor: STATUS.warning.bg,
        color: STATUS.warning.fg,
        border: `1px solid #F5DDB0`,
      }}
    >
      {count} unpriced change{count === 1 ? "" : "s"}
    </Box>
  ) : null;

const COMPACT = {
  height: 36,
  minHeight: 36,
  padding: "0 14px",
  fontSize: 13,
  "& svg": { fontSize: 16 },
};

/** The header's primary action (Save as deal) — the sizer button, compact. */
export const WsPrimaryButton = ({ sx, ...props }) => (
  <PrimaryButton {...props} sx={{ ...COMPACT, ...sx }} />
);

/** The header's secondary buttons (version picker, ⋯) — compact outlined. */
export const WsSecondaryButton = ({ sx, ...props }) => (
  <SecondaryButton {...props} sx={{ ...COMPACT, ...sx }} />
);
